// =====================================================
// TARIFFS API
// CRUD para tarifas con gestión de actos y precios
// =====================================================

import { api } from './client';

export interface Tariff {
  id: string;
  companyId: string;
  policyId: string;
  policyCode?: string;
  policyName?: string;
  insuranceCompanyName?: string;
  code: string;
  name: string;
  description: string | null;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
  actsCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TariffSelect {
  id: string;
  code: string;
  name: string;
  policyId: string;
  policyName?: string;
  validFrom: string;
  validTo: string | null;
}

export interface TariffAct {
  id: string;
  tariffId: string;
  medicalActId: string;
  medicalActCode: string;
  medicalActName: string;
  treatmentGroupName?: string;
  defaultPrice: number;
  price: number;
  requiresOdontogram: boolean;
}

export interface UnpricedAct {
  id: string;
  code: string;
  name: string;
  treatmentGroupName?: string;
}

export interface CreateTariffRequest {
  policyId: string;
  code: string;
  name: string;
  description?: string;
  validFrom: string;
  validTo?: string;
}

export interface UpdateTariffRequest {
  policyId?: string;
  code?: string;
  name?: string;
  description?: string;
  validFrom?: string;
  validTo?: string | null;
  isActive?: boolean;
}

export interface CreateTariffActRequest {
  medicalActId: string;
  price: number;
}

export interface UpdateTariffActRequest {
  price: number;
}

export interface BatchUpdateTariffActsRequest {
  acts: Array<{
    medicalActId: string;
    price: number;
  }>;
}

const BASE_URL = '/tariffs';

export const tariffsApi = {
  /**
   * Obtener lista para select (solo activas y vigentes)
   */
  getForSelect: (policyId?: string): Promise<TariffSelect[]> => {
    const query = policyId ? `?policyId=${policyId}` : '';
    return api.get<TariffSelect[]>(`${BASE_URL}/select${query}`);
  },

  /**
   * Obtener todas las tarifas
   */
  getAll: (includeInactive = false, policyId?: string): Promise<Tariff[]> => {
    const params = new URLSearchParams();
    if (includeInactive) params.set('includeInactive', 'true');
    if (policyId) params.set('policyId', policyId);
    const query = params.toString();
    return api.get<Tariff[]>(`${BASE_URL}${query ? `?${query}` : ''}`);
  },

  /**
   * Obtener una tarifa por ID
   */
  getById: (id: string): Promise<Tariff> => {
    return api.get<Tariff>(`${BASE_URL}/${id}`);
  },

  /**
   * Crear nueva tarifa (solo admin)
   */
  create: (data: CreateTariffRequest): Promise<Tariff> => {
    return api.post<Tariff>(BASE_URL, data);
  },

  /**
   * Actualizar tarifa (solo admin)
   */
  update: (id: string, data: UpdateTariffRequest): Promise<Tariff> => {
    return api.patch<Tariff>(`${BASE_URL}/${id}`, data);
  },

  /**
   * Desactivar tarifa (solo admin)
   */
  deactivate: (id: string): Promise<Tariff> => {
    return api.delete<Tariff>(`${BASE_URL}/${id}`);
  },

  /**
   * Reactivar tarifa (solo admin)
   */
  reactivate: (id: string): Promise<Tariff> => {
    return api.patch<Tariff>(`${BASE_URL}/${id}/reactivate`);
  },

  // =========================================
  // Gestión de actos en tarifa
  // =========================================

  /**
   * Obtener actos de una tarifa
   */
  getTariffActs: (tariffId: string): Promise<TariffAct[]> => {
    return api.get<TariffAct[]>(`${BASE_URL}/${tariffId}/acts`);
  },

  /**
   * Añadir acto a tarifa
   */
  addTariffAct: (tariffId: string, data: CreateTariffActRequest): Promise<TariffAct> => {
    return api.post<TariffAct>(`${BASE_URL}/${tariffId}/acts`, data);
  },

  /**
   * Actualizar precio de acto en tarifa
   */
  updateTariffAct: (tariffId: string, actId: string, data: UpdateTariffActRequest): Promise<TariffAct> => {
    return api.patch<TariffAct>(`${BASE_URL}/${tariffId}/acts/${actId}`, data);
  },

  /**
   * Eliminar acto de tarifa
   */
  removeTariffAct: (tariffId: string, actId: string): Promise<{ success: boolean }> => {
    return api.delete<{ success: boolean }>(`${BASE_URL}/${tariffId}/acts/${actId}`);
  },

  /**
   * Actualización masiva de actos en tarifa
   */
  batchUpdateTariffActs: (tariffId: string, data: BatchUpdateTariffActsRequest): Promise<{ success: boolean; updated: number }> => {
    return api.patch<{ success: boolean; updated: number }>(`${BASE_URL}/${tariffId}/acts/batch`, data);
  },

  /**
   * Obtener actos sin valorar (sin precio base, sin precio centro, sin tarifa)
   */
  getUnpricedActs: (): Promise<UnpricedAct[]> => {
    return api.get<UnpricedAct[]>(`${BASE_URL}/unpriced-acts`);
  },
};

export default tariffsApi;
